import { CbhFeatureFlag } from "@src/appV2/FeatureFlags/CbhFeatureFlag";
import { FEATURE_FLAG_DEFAULT_VALUES } from "@src/appV2/FeatureFlags/constants";
import { useCbhFlag } from "@src/appV2/FeatureFlags/useCbhFlag";
import {
  type AcceptableUploadImageContentTypes,
  base64ToBlob,
  isAcceptableImageUploadContentType,
  isAcceptablePdfUploadContentType,
} from "@src/appV2/lib/files";
import { type UploadFileData } from "@src/appV2/lib/Media/types";
import { getDocumentFileName } from "@src/appV2/redesign/Documents/Details/Upload/getDocumentFileName";
import { useUploadFileToFileStorage } from "@src/appV2/redesign/Documents/Details/Upload/useUploadFileToFileStorage";
import {
  validateAndGetUploadFile,
  validateAndGetUploadFileWithMerging,
} from "@src/appV2/redesign/Documents/Details/Upload/validateAndGetUploadFile";
import { useDefinedWorker } from "@src/appV2/Worker/useDefinedWorker";
import { useMutation } from "@tanstack/react-query";

import { useCreateDocument } from "../../api/useCreateDocument";
import { type UploadedFile } from "../../useFileUploaderContext";

interface UploadDocumentParams {
  requirementId: string;
  requirementName: string;
  subType?: string;
  files: UploadedFile[];
}

export function useDocumentUploadFlow() {
  const worker = useDefinedWorker();

  const { mutateAsync: uploadFileToFileStorage } = useUploadFileToFileStorage();
  const { mutateAsync: createDocument } = useCreateDocument();

  const configDocumentUploadMultiSelect = useCbhFlag(
    CbhFeatureFlag.CONFIG_DOCUMENT_UPLOAD_MULTI_SELECT,
    {
      defaultValue: FEATURE_FLAG_DEFAULT_VALUES[CbhFeatureFlag.CONFIG_DOCUMENT_UPLOAD_MULTI_SELECT],
    }
  );

  return useMutation({
    mutationFn: async (params: UploadDocumentParams) => {
      const { requirementId, requirementName, files } = params;
      const filesToUpload: UploadFileData[] = await Promise.all(
        files.map(async (file) => ({
          dataUrlBase64: file.dataUrlBase64,
          contentType: file.contentType as AcceptableUploadImageContentTypes,
          extension: file.extension,
          blob: await base64ToBlob(file.dataUrlBase64),
        }))
      );

      // Use enhanced validation with PDF merging if feature flag is enabled,
      // otherwise use the original validation logic
      const shouldMergeFiles =
        configDocumentUploadMultiSelect.mergePdf &&
        files.every(
          (file) =>
            isAcceptableImageUploadContentType(file.contentType) ||
            isAcceptablePdfUploadContentType(file.contentType)
        );
      const fileToUpload = shouldMergeFiles
        ? await validateAndGetUploadFileWithMerging(filesToUpload)
        : await validateAndGetUploadFile(filesToUpload);

      const { fileStorageFileKey, fileStorageRootFolder } = await uploadFileToFileStorage({
        userId: worker.userId,
        requirementId,
        file: fileToUpload,
      });

      const fileName = fileStorageFileKey?.split("/").pop();

      return await createDocument({
        data: {
          attributes: {
            workerUserId: worker.userId,
            name: getDocumentFileName({ requirementName, fileName }),
            fileStorageFileKey,
            fileStorageRootFolder,
            subType: "",
            requirementId,
          },
        },
      });
    },
  });
}
